import { Button, Card, ListGroup, ListGroupItem, Table } from "react-bootstrap";
import { flexRender } from "@tanstack/react-table";
import { CrudTableModel } from "./crudTableModel";
import { CrudTablePagination } from "./crudTablePagination";
import { useMediaQuery } from "usehooks-ts";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp } from "react-bootstrap-icons";
import { HashLink } from "react-router-hash-link";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import {
  addAllIds,
  clearSelectedIds,
  removeAllIds,
} from "../../../infrastructure/slices/bulkSelectSlice/bulkSelectSlice";
import { useAppDispatch } from "../../../../../hooks/useTypedSelector";
import { useLocation } from "react-router-dom";
import React from "react";
import { extractDealerInfo } from "../../../utils/extractDealerInfo";

export function CrudTable({
  table,
  prefix,
  showBulkPopup,
  setShowBulkPopup,
  showBulkSticker,
  disablePagination = false,
  subTable,
  disableSelectAll = false,
  dealerPath,
}: CrudTableModel & { disableSelectAll?: boolean }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pageName = location.pathname.split("/").pop();
  const isMobile = useMediaQuery("(max-width: 991.98px)");
  const selectedIds = useSelector(
    (state: RootState) =>
      state.bulkSelected.pageSelection.find((entry) => entry.page === pageName)
        ?.ids || [],
  );
  const { t } = useTranslation("common");
  const allIds = table
    .getPrePaginationRowModel()
    .rows.map((row) => row.original.id);

  const toggleBulkPopup = () => {
    if (!setShowBulkPopup) return;
    document.body.style.overflow = showBulkPopup ? "auto" : "hidden";
    setShowBulkPopup(!showBulkPopup);
  };

  return (
    <>
      <div className={"d-flex flex-column flex-md-row"}>
        {selectedIds && selectedIds.length > 0 && (
          <div
            className={"d-flex flex-column flex-sm-row mb-2"}
            style={{ height: "30px" }}
          >
            <Button
              size={"sm"}
              className={"responsive-button mb-2 mb-sm-0 me-0 me-sm-2"}
              onClick={() =>
                dispatch(
                  clearSelectedIds({
                    page: pageName as string,
                  }),
                )
              }
            >
              {t("Clear selection")} ({selectedIds.length})
            </Button>
            <Button
              size={"sm"}
              className={"responsive-button mb-2 mb-sm-0 me-0 me-sm-2"}
              onClick={toggleBulkPopup}
            >
              {t("Bulk form")}
            </Button>

            {typeof showBulkSticker === "function" && (
              <Button
                size={"sm"}
                className={"responsive-button mb-2 mb-sm-0 me-0 me-sm-2"}
                onClick={showBulkSticker}
              >
                {t("Open sticker")}
              </Button>
            )}
          </div>
        )}

        {!disablePagination && (
          <CrudTablePagination
            current={table.getState().pagination.pageIndex}
            count={table.getPageCount()}
            getCanPreviousPage={table.getCanPreviousPage}
            firstPage={table.firstPage}
            previousPage={table.previousPage}
            setPageIndex={table.setPageIndex}
            getCanNextPage={table.getCanNextPage}
            nextPage={table.nextPage}
            lastPage={table.lastPage}
          />
        )}
      </div>
      <Table className={`crud-table align-middle table-${prefix}`}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                if (header.id === "select") {
                  return (
                    <th
                      key={header.id}
                      style={{ width: "25px", marginTop: "4px" }}
                    >
                      {!disableSelectAll ? (
                        <input
                          className="selectable-input-header"
                          type="checkbox"
                          checked={allIds.every((id) =>
                            selectedIds?.includes(id),
                          )}
                          onChange={() => {
                            if (
                              !allIds.every((id) => selectedIds?.includes(id))
                            ) {
                              // Extract dealer info for all rows
                              const dealerInfos = dealerPath
                                ? table
                                    .getPrePaginationRowModel()
                                    .rows.map((row) => ({
                                      id: row.original.id,
                                      dealerInfo: extractDealerInfo(
                                        row.original as Record<string, unknown>,
                                        dealerPath,
                                      ),
                                    }))
                                : undefined;

                              dispatch(
                                addAllIds({
                                  ids: allIds,
                                  page: pageName as string,
                                  dealerInfos,
                                }),
                              );
                            } else {
                              dispatch(
                                removeAllIds({
                                  ids: allIds,
                                  page: pageName as string,
                                }),
                              );
                            }
                          }}
                        />
                      ) : null}
                    </th>
                  );
                }

                return (
                  <th
                    key={header.id}
                    {...(header.column.getCanSort()
                      ? {
                          onClick: header.column.getToggleSortingHandler(),
                        }
                      : {})}
                    style={
                      header.column.getCanSort() ? { cursor: "pointer" } : {}
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : t(
                          "crud.table." +
                            prefix +
                            "." +
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            ),
                        )}

                    {header.column.getIsSorted() === "asc" ? (
                      <ArrowUp />
                    ) : header.column.getIsSorted() === "desc" ? (
                      <ArrowDown />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              if (!isMobile) {
                return (
                  <>
                    <tr
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => {
                        let id = cell.row?.original?.id;
                        if (cell.row.original.car !== undefined) {
                          id = cell.row?.original?.car.id;
                        }

                        return (
                          <td key={cell.id} className={"position-relative"}>
                            {cell.column.columnDef?.meta?.columnAction !=
                              undefined &&
                            cell.column.columnDef?.meta?.columnAction !=
                              "disabled" &&
                            cell.column.columnDef?.meta?.columnAction != "" ? (
                              <HashLink
                                to={cell.column.columnDef?.meta?.columnAction.replace(
                                  ":id",
                                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                  id?.toString(),
                                )}
                                className={"stretched-link"}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </HashLink>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {subTable && (
                      <tr>
                        {/* 2nd row is a custom 1 cell row */}
                        <td colSpan={row.getVisibleCells().length}>
                          {subTable({ row, t })}
                        </td>
                      </tr>
                    )}
                  </>
                );
              }

              const cells = row.getVisibleCells();
              const headers = table.getHeaderGroups()[0].headers;

              const headerCell = cells[0];
              const actionsCell = cells[cells.length - 1];

              return (
                <tr key={row.id}>
                  <Card className="mb-3">
                    <Card.Header key={headerCell.id}>
                      {flexRender(
                        headerCell.column.columnDef.cell,
                        headerCell.getContext(),
                      )}
                    </Card.Header>
                    <ListGroup className="list-group-flush">
                      {cells
                        .filter(
                          (cell, key) =>
                            key != 0 &&
                            key != cells.length - 1 &&
                            cell.getValue() !== undefined,
                        )
                        .map((cell) => {
                          const header = headers.find((header) =>
                            cell.id.includes(header.id),
                          );

                          let id = cell.row?.original?.id;
                          if (cell.row.original.car !== undefined) {
                            id = cell.row?.original?.car.id;
                          }

                          return (
                            <ListGroupItem key={cell.id}>
                              {header === undefined || header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  ) + ": "}

                              {cell.column.columnDef?.meta?.columnAction !=
                                undefined &&
                              cell.column.columnDef?.meta?.columnAction !=
                                "disabled" ? (
                                <HashLink
                                  to={cell.column.columnDef?.meta?.columnAction.replace(
                                    ":id",
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                    id?.toString(),
                                  )}
                                  className={"stretched-link"}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </HashLink>
                              ) : (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )
                              )}
                            </ListGroupItem>
                          );
                        })}
                    </ListGroup>
                    <Card.Body>
                      {flexRender(
                        actionsCell.column.columnDef.cell,
                        actionsCell.getContext(),
                      )}
                    </Card.Body>
                  </Card>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                {t("No results")}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {!disablePagination && (
        <CrudTablePagination
          current={table.getState().pagination.pageIndex}
          count={table.getPageCount()}
          getCanPreviousPage={table.getCanPreviousPage}
          firstPage={table.firstPage}
          previousPage={table.previousPage}
          setPageIndex={table.setPageIndex}
          getCanNextPage={table.getCanNextPage}
          nextPage={table.nextPage}
          lastPage={table.lastPage}
        />
      )}
    </>
  );
}
